import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Navigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import LoadingScreen from '../../Components/LoadingScreen/LoadingScreen';
import {
    ACCESS_DENIAL_REASON,
    SECURITY_STATUS,
    useSecurity
} from '../../Contexts/SecurityContext';
import './AccessDeniedPage.css';

const contentByReason = {
    [ACCESS_DENIAL_REASON.NOT_REGISTERED]: {
        title: 'Application access is not enabled',
        message: 'Your Microsoft account was authenticated, but it is not registered in this application. Contact a system administrator to request access.'
    },
    [ACCESS_DENIAL_REASON.INACTIVE]: {
        title: 'Your application account is inactive',
        message: 'Your Microsoft account was authenticated, but your application access has been deactivated. Contact a system administrator for assistance.'
    },
    [ACCESS_DENIAL_REASON.NO_SEAT]: {
        title: 'No license seat is assigned',
        message: 'Your Microsoft account was authenticated, but it does not have a valid seat in the current license. Contact a system administrator for assistance.'
    },
    [ACCESS_DENIAL_REASON.LICENSE_INACTIVE]: {
        title: 'The application license is not active',
        message: 'Your Microsoft account was authenticated, but the application license is not currently valid. Contact a system administrator for assistance.'
    },
    permission_required: {
        title: 'Permission required',
        message: 'You are signed in, but you do not have permission to access this functionality.'
    },
    no_permissions: {
        title: 'No functionality is currently assigned',
        message: 'Your account is active and licensed, but it does not have any application functionality assigned. Contact a security administrator for assistance.'
    },
    [ACCESS_DENIAL_REASON.UNKNOWN]: {
        title: 'Access denied',
        message: 'Your Microsoft account was authenticated, but the application API denied access. Contact a system administrator for assistance.'
    }
};

const AccessDeniedPage = () => {
    const location = useLocation();
    const { instance } = useMsal();
    const { status, denialReason, account, refreshSecurity } = useSecurity();
    const routeReason = location.state?.reason;
    const reason = routeReason || denialReason || ACCESS_DENIAL_REASON.UNKNOWN;
    const content = contentByReason[reason] || contentByReason[ACCESS_DENIAL_REASON.UNKNOWN];

    if (status === SECURITY_STATUS.RESOLVING) return <LoadingScreen />;
    if (status === SECURITY_STATUS.UNAUTHENTICATED) return <Navigate to="/" replace />;
    const isFunctionalDenial = reason === 'permission_required' || reason === 'no_permissions';
    if (status === SECURITY_STATUS.AUTHORIZED && !isFunctionalDenial) {
        return <Navigate to="/home" replace />;
    }

    const isVerificationError = status === SECURITY_STATUS.ERROR;

    return (
        <div className="access-denied-page d-flex align-items-center justify-content-center">
            <div className="card access-denied-card">
                <div className="card-body p-5">
                    <p className="text-uppercase small fw-semibold text-secondary mb-2">Application security</p>
                    <h2>{isVerificationError ? 'We could not verify your access' : content.title}</h2>
                    <p className="mb-4">
                        {isVerificationError
                            ? 'The application API could not be reached or returned an unexpected response. Retry the verification or sign out.'
                            : content.message}
                    </p>
                    {account?.username && (
                        <Alert variant="light" className="mb-4">
                            Signed in as <strong>{account.username}</strong>
                        </Alert>
                    )}
                    <div className="d-flex flex-wrap gap-2">
                        {(isVerificationError || reason === ACCESS_DENIAL_REASON.UNKNOWN) && (
                            <Button variant="primary" onClick={refreshSecurity}>Retry verification</Button>
                        )}
                        <Button variant="outline-secondary" onClick={() => instance.logoutRedirect()}>
                            Sign out
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccessDeniedPage;
